import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import commonStrings from 'modules/common/strings';
import { useOnClickOutside } from 'utils/functions/hooks';
import {
  Wrapper,
  SelectWrapper,
  Select,
  TextInput,
  Icon,
  Envelop,
  EnvelopItem,
  FilterDropDownCheckbox,
  TextInputWrapper,
  FitlerActionsWrapper,
  FitlerActionBtn,
  EnvelopWrapper,
} from './MultiSelectionDropdownFitler.style';
import Loader from '../Loader/Loader';

// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################
const MultiSelectionDropdownFitler = ({
  dropdownMenuItemsList,
  dropdownMenuSelectedArr,
  dropdownMenuPlaceholder,
  disabled,
  handleDropdownMenuChange,
  objectMapper,
  showLoader,
}) => {
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  const dropdownMenuWrapperRef = useRef(null);
  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  const [isMenuEnvelopOpen, setIsMenuEnvelopOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filteredDropdownMenuItemsList, setFilteredDropdownMenuItemsList] = useState([]);
  const [updateSelectedItems, setUpdateSelectedItems] = useState([...dropdownMenuSelectedArr]);
  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  useEffect(() => {
    setUpdateSelectedItems([...dropdownMenuSelectedArr]);
  }, [dropdownMenuSelectedArr, isMenuEnvelopOpen]);

  useEffect(() => {
    if (dropdownMenuItemsList?.length > 0)
      setFilteredDropdownMenuItemsList([...dropdownMenuItemsList]);
    else setFilteredDropdownMenuItemsList([]);
  }, [dropdownMenuItemsList]);

  useEffect(() => {
    if (dropdownMenuItemsList?.length > 0) {
      const filteredData = dropdownMenuItemsList.filter(item =>
        item[objectMapper.name].toLowerCase().includes(searchText.toLowerCase()),
      );
      setFilteredDropdownMenuItemsList([...filteredData]);
    }
  }, [dropdownMenuItemsList, objectMapper.name, searchText]);

  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  useOnClickOutside(
    dropdownMenuWrapperRef,
    () => {
      setIsMenuEnvelopOpen(false);
      setSearchText('');
      setFilteredDropdownMenuItemsList([...dropdownMenuItemsList]);
    },
    isMenuEnvelopOpen,
  );
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################
  const handleMenuSelectClick = () => {
    if (disabled) return;

    setIsMenuEnvelopOpen(prevState => !prevState);
  };

  const handleDropdownMenuItemClick = selectedObj => {
    if (
      updateSelectedItems.some(selecteditemKey => selecteditemKey === selectedObj.key) === false
    ) {
      setUpdateSelectedItems([...updateSelectedItems, selectedObj.key]);
    } else {
      const selectedItemIndex = updateSelectedItems.findIndex(
        selecteditemKey => selecteditemKey === selectedObj.key,
      );
      if (selectedItemIndex !== -1) {
        const tempUpdateSelectedItems = [
          ...updateSelectedItems.slice(0, selectedItemIndex),
          ...updateSelectedItems.slice(selectedItemIndex + 1),
        ];
        setUpdateSelectedItems(tempUpdateSelectedItems);
      }
    }
  };
  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################
  return (
    <Wrapper>
      <SelectWrapper ref={dropdownMenuWrapperRef}>
        <Select
          data-testid="dropdown-menu-select"
          disabled={disabled}
          onClick={handleMenuSelectClick}
        >
          <div>
            {dropdownMenuSelectedArr?.length === 0
              ? `Select ${dropdownMenuPlaceholder}`
              : `${dropdownMenuPlaceholder} (${dropdownMenuSelectedArr?.length})`}
          </div>
          {showLoader === false ? (
            <Icon icon={'"\\e913"'} data-testid="dropdown-menu-icon" />
          ) : (
            <Loader data-testid="dropdown-menu-loader" width={14} height={14} thickness={3} />
          )}
        </Select>
        {!showLoader && isMenuEnvelopOpen && (
          <EnvelopWrapper data-testid="dropdown-menu-envelope">
            <TextInputWrapper>
              <Icon icon={'"\\e905"'} />
              <TextInput
                data-testid="dropdown-menu-text"
                type="text"
                value={searchText}
                placeholder={`Search ${dropdownMenuPlaceholder}`}
                onChange={event => setSearchText(event.target.value)}
              />
              {searchText && (
                <Icon
                  icon={'"\\e91f"'}
                  onClick={() => {
                    setSearchText('');
                    setFilteredDropdownMenuItemsList([...dropdownMenuItemsList]);
                  }}
                />
              )}
            </TextInputWrapper>
            <Envelop>
              {filteredDropdownMenuItemsList.map(menuItem => {
                const isChecked = updateSelectedItems?.findIndex(
                  selectedKey => selectedKey === menuItem[objectMapper.key],
                );
                return (
                  <EnvelopItem
                    data-testid={menuItem[objectMapper.key]}
                    key={menuItem[objectMapper.key]}
                    onClick={() =>
                      handleDropdownMenuItemClick({
                        key: menuItem[objectMapper.key],
                        name: menuItem[objectMapper.name],
                      })
                    }
                  >
                    <FilterDropDownCheckbox
                      type="checkbox"
                      readOnly
                      value={menuItem[objectMapper.key]}
                      checked={isChecked !== -1}
                    />
                    {menuItem[objectMapper.name]}
                  </EnvelopItem>
                );
              })}
            </Envelop>
            <FitlerActionsWrapper>
              <FitlerActionBtn
                onClick={() => {
                  handleDropdownMenuChange(updateSelectedItems);
                  setIsMenuEnvelopOpen(false);
                }}
              >
                {commonStrings.multiSelectDropdownFilter.applyBtn}
              </FitlerActionBtn>
              <FitlerActionBtn
                onClick={() => {
                  handleDropdownMenuChange('all');
                  setUpdateSelectedItems([]);
                  setSearchText('');
                  setFilteredDropdownMenuItemsList([...dropdownMenuItemsList]);
                }}
              >
                {commonStrings.multiSelectDropdownFilter.cancelBtn}
              </FitlerActionBtn>
            </FitlerActionsWrapper>
          </EnvelopWrapper>
        )}
      </SelectWrapper>
    </Wrapper>
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

MultiSelectionDropdownFitler.propTypes = {
  dropdownMenuItemsList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  dropdownMenuSelectedArr: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      name: PropTypes.string,
    }),
  ).isRequired,
  dropdownMenuPlaceholder: PropTypes.string,
  disabled: PropTypes.bool,
  handleDropdownMenuChange: PropTypes.func.isRequired,
  objectMapper: PropTypes.shape({
    key: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
  }).isRequired,
  showLoader: PropTypes.bool,
};

MultiSelectionDropdownFitler.defaultProps = {
  dropdownMenuPlaceholder: '',
  disabled: false,
  showLoader: false,
};

export default MultiSelectionDropdownFitler;
